import { z } from "zod";

/**
 * Clinic consumable supplies — cost-control / availability tracking for the
 * materials a clinic burns through. No sell price (not a sellable catalogue);
 * stock status + expiry are computed (pure) from on-hand vs reorder level.
 */

export const supplyCategorySchema = z.enum([
	"consumable",
	"medication",
	"instrument",
	"ppe",
	"other",
]);
export type SupplyCategory = z.infer<typeof supplyCategorySchema>;

export const clinicSupplyApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	name: z.string(),
	category: supplyCategorySchema,
	unit: z.string().nullable(),
	quantity_on_hand: z.number().int(),
	reorder_level: z.number().int(),
	expiry_date: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/)
		.nullable(),
	notes: z.string().nullable(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type ClinicSupplyApi = z.infer<typeof clinicSupplyApiSchema>;

export const createClinicSupplyInputSchema = z.object({
	name: z.string().min(1).max(200),
	category: supplyCategorySchema.default("consumable"),
	unit: z.string().max(60).optional(),
	quantity_on_hand: z.coerce.number().int().min(0).max(10_000_000).default(0),
	reorder_level: z.coerce.number().int().min(0).max(1_000_000).default(0),
	expiry_date: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/)
		.optional(),
	notes: z.string().max(500).optional(),
});
export type CreateClinicSupplyInput = z.infer<
	typeof createClinicSupplyInputSchema
>;

export const updateClinicSupplyInputSchema = z.object({
	name: z.string().min(1).max(200).optional(),
	category: supplyCategorySchema.optional(),
	unit: z.string().max(60).nullable().optional(),
	reorder_level: z.coerce.number().int().min(0).max(1_000_000).optional(),
	expiry_date: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/)
		.nullable()
		.optional(),
	notes: z.string().max(500).nullable().optional(),
});
export type UpdateClinicSupplyInput = z.infer<
	typeof updateClinicSupplyInputSchema
>;

export const adjustClinicSupplyInputSchema = z.object({
	supply_id: z.string().uuid(),
	delta: z.coerce
		.number()
		.int()
		.refine((n) => n !== 0, "delta must be non-zero"),
	reason: z
		.enum(["received", "used", "wastage", "correction", "expired"])
		.default("correction"),
	notes: z.string().max(300).optional(),
});
export type AdjustClinicSupplyInput = z.infer<
	typeof adjustClinicSupplyInputSchema
>;

export type SupplyStockStatus = "out" | "low" | "ok";

/** Stock level vs reorder threshold. Pure. */
export function supplyStockStatus(
	quantityOnHand: number,
	reorderLevel: number,
): SupplyStockStatus {
	if (quantityOnHand <= 0) return "out";
	if (quantityOnHand <= reorderLevel) return "low";
	return "ok";
}
